import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Car, Ship, MapPin, Calendar, Gauge, ArrowRight, CheckCircle, Anchor } from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import VehicleCard from './VehicleCard';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';
import { API } from '../../config';
import { SHIP_COUNTRIES } from '../../data/shipCountries';

const ShippedVehicles = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShippedVehicles();
  }, []);

  const fetchShippedVehicles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/public/shipped-vehicles`);
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Error fetching shipped vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const countryCounts = SHIP_COUNTRIES.reduce((acc, c) => {
    acc[c.slug] = vehicles.filter((v) => v.ship_to_country === c.slug).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Recently Shipped Vehicles - Global Trade Car"
        description="Browse our recently shipped vehicles. See real examples of Japanese used cars exported worldwide. Global Trade Car - your trusted Japan car exporter."
        keywords="shipped vehicles, exported cars, Japan car export, recently shipped, vehicle delivery, Global Trade Car"
      />
      <PublicHeader />

      {/* Hero Section */}
      <div className="relative overflow-hidden text-white">
        <img
          src="/All_Shipped_Vehicles.png"
          alt="Recently shipped vehicles"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex items-center gap-3 mb-4">
            <Ship className="h-8 w-8 text-cyan-400" />
            <Badge className="bg-cyan-500 text-white text-sm px-3 py-1">
              Recently Shipped
            </Badge>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Vehicles on Their Way
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mb-8">
            Delivering quality vehicles to happy drivers worldwide. Explore our 
            recently completed shipments and see the standards we uphold. Find your 
            dream car and let us handle the journey to your doorstep.
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span>Real shipped vehicles</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <MapPin className="h-4 w-4 text-cyan-400" />
              <span>Worldwide delivery</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Calendar className="h-4 w-4 text-amber-400" />
              <span>Recently completed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Destination Countries */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-3 mb-2">
            <Anchor className="h-6 w-6 text-cyan-600" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              Where Are We Shipping?
            </h2>
          </div>
          <p className="text-slate-500 mb-8 max-w-2xl">
            Select a destination to see the vehicles currently on their way.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {SHIP_COUNTRIES.map((c) => {
              const count = countryCounts[c.slug] || 0;
              return (
                <button
                  key={c.slug}
                  onClick={() => navigate(`/shipped/${c.slug}`)}
                  className={`group relative overflow-hidden rounded-xl bg-gradient-to-br ${c.gradient} p-5 text-left text-white shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
                >
                  <img
                    src={c.flag}
                    alt={`${c.name} flag`}
                    className="h-8 w-auto mb-3 rounded ring-2 ring-white/40 shadow"
                  />
                  <h3 className="font-bold text-base group-hover:underline">{c.name}</h3>
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] font-semibold bg-black/25 rounded-full px-2 py-0.5">
                    <Ship className="h-3 w-3" />
                    {count} {count === 1 ? 'vehicle' : 'vehicles'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {!loading && vehicles.length > 0 && (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <p className="text-slate-600">
                <span className="font-bold text-slate-900">{vehicles.length}</span> vehicles shipped
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/vehicles')}
                className="text-cyan-600 border-cyan-200 hover:bg-cyan-50"
              >
                Browse Available Vehicles
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Vehicles Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-48 bg-gray-200"></div>
                <CardContent className="p-6">
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : vehicles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((vehicle) => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} showShipCountry showEta />
            ))}
          </div>
        ) : (
          <Card className="mt-8">
            <CardContent className="p-12 text-center">
              <Ship className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No Shipped Vehicles Yet
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                We haven't shipped any vehicles yet. Check back soon or browse our 
                available inventory to be the first!
              </p>
              <Button
                onClick={() => navigate('/vehicles')}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
              >
                Browse Available Vehicles
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* CTA Section */}
      {vehicles.length > 0 && (
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Ship Your Next Vehicle?</h2>
            <p className="text-lg text-cyan-100 mb-6 max-w-2xl mx-auto">
              Join our growing list of satisfied customers. Browse our inventory and 
              start your export journey today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/vehicles')}
                className="bg-white text-cyan-700 hover:bg-cyan-50 font-semibold"
              >
                Browse Inventory
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/contact')}
                className="border-white text-white hover:bg-white/10 font-semibold"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      )}

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default ShippedVehicles;
